import { formatMoney, formatNumber, SearchSelect, stockHealthTone } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { indexOptions } from '../catalog/options.ts';
import { useOptions } from '../catalog/useCatalogQueries.ts';
import { usePageContext } from '../usePageContext.ts';
import type { SaleableVariant } from './new-order-form.ts';
import { useVariantOptions } from './useVariantOptions.ts';
import { useVariantScan } from './useVariantScan.ts';
import { useSearchSelectTexts } from './useSearchSelectTexts.ts';
import styles from './NewOrder.module.css';

const NOTICE_STYLES = { success: 'scanAdded', warning: 'scanWarning' } as const;

export type VariantPickerProps = {
  api: TypedClient;
  company: string;
  locationId: string;
  currency: string;
  addedIds: ReadonlySet<string>;
  onAdd: (variant: SaleableVariant) => void;
};

export const VariantPicker = ({
  api,
  company,
  locationId,
  currency,
  addedIds,
  onAdd,
}: VariantPickerProps) => {
  const { t, locale } = usePageContext();
  const texts = useSearchSelectTexts(t('newOrder.noProducts'));
  const options = useOptions(api);
  const index = indexOptions(options.data?.items ?? [], locale);
  const scan = useVariantScan({ api, locationId, company, index, addedIds, onAdd });
  const [text, setText] = useState('');
  const [opened, setOpened] = useState(false);
  const [clearedAt, setClearedAt] = useState(scan.scans);
  if (clearedAt !== scan.scans) {
    setClearedAt(scan.scans);
    setText('');
  }
  const found = useVariantOptions({ api, company, locationId, index, text, scanned: scan.scanned, opened });

  const describe = (variant: SaleableVariant) => {
    const available = found.availableOf(variant.variantId);
    return {
      value: variant.variantId,
      label: variant.label,
      detail: addedIds.has(variant.variantId) ? `${variant.sku} · ${t('newOrder.added')}` : variant.sku,
      aside: formatMoney({ amount: variant.price, currency }, locale),
      badge: {
        label: t('newOrder.available', { quantity: formatNumber(available, locale) }),
        tone: stockHealthTone[available === 0 ? 'out' : 'ok'],
      },
      disabled: available === 0,
    };
  };

  return (
    <div className={styles['picker']}>
      <SearchSelect
        label={t('newOrder.searchProducts')}
        placeholder={t('newOrder.productPlaceholder')}
        texts={texts}
        hint={t('newOrder.productHelp')}
        query={text}
        options={found.variants.map(describe)}
        loading={found.loading}
        hasMore={found.hasMore}
        loadingMore={found.loadingMore}
        stayOpenOnPick
        onOpen={() => {
          setOpened(true);
        }}
        onQueryChange={(next) => {
          scan.clear();
          setText(next);
        }}
        onSubmitQuery={scan.search}
        onLoadMore={found.loadMore}
        onPick={(variantId) => {
          const variant = found.variantOf(variantId);
          if (variant !== undefined) onAdd(variant);
        }}
      />
      {scan.notice === null ? null : (
        <p role="status" className={styles[NOTICE_STYLES[scan.notice.tone]]}>
          {scan.notice.message}
        </p>
      )}
    </div>
  );
};
